/**
 * LoginPageView — pure presentation component for the WebAuthn login/register UI.
 * No logic; receives all state and callbacks from LoginPage.
 */
import './LoginPage.css'; // rationale: co-located styles for auth page

type Mode = 'register' | 'login';
type Status = 'idle' | 'loading' | 'success' | 'error';

interface LoginPageViewProps {
    mode: Mode;
    accountIdInput: string;
    status: Status;
    errorMessage: string | null;
    onModeChange: (mode: Mode) => void;
    onAccountIdChange: (value: string) => void;
    onRegister: () => void;
    onLogin: () => void;
}

const LoginPageView = ({
    mode,
    accountIdInput,
    status,
    errorMessage,
    onModeChange,
    onAccountIdChange,
    onRegister,
    onLogin,
}: LoginPageViewProps) => {
    console.log('LoginPageView is running');
    const busy = status === 'loading'; // disable buttons during in-flight requests

    console.log('LoginPageView is returning auth form');
    return (
        <main className="login-page-container" aria-label="Authentication">
            <div className="login-page-card">
                <h1 className="login-page-title">QreditCharge</h1>
                <p className="login-page-subtitle">Sign in to access Admin &amp; Analytics</p>

                {/* Mode toggle */}
                <div className="login-page-tabs" role="tablist">
                    <button
                        role="tab"
                        aria-selected={mode === 'register'}
                        className={`login-tab${mode === 'register' ? ' login-tab--active' : ''}`}
                        onClick={() => onModeChange('register')}
                        disabled={busy}
                    >
                        Create Account
                    </button>
                    <button
                        role="tab"
                        aria-selected={mode === 'login'}
                        className={`login-tab${mode === 'login' ? ' login-tab--active' : ''}`}
                        onClick={() => onModeChange('login')}
                        disabled={busy}
                    >
                        Sign In
                    </button>
                </div>

                {/* Register panel */}
                {mode === 'register' && (
                    <div className="login-page-panel" role="tabpanel">
                        <p className="login-page-description">
                            Register a new anonymous account using a passkey stored on your device. No password required.
                        </p>
                        <button
                            className="login-page-btn"
                            onClick={onRegister}
                            disabled={busy}
                            aria-busy={busy}
                        >
                            {busy ? 'Registering…' : 'Create Account with Passkey'}
                        </button>
                    </div>
                )}

                {/* Login panel */}
                {mode === 'login' && (
                    <div className="login-page-panel" role="tabpanel">
                        <p className="login-page-description">
                            Enter your Account ID and authenticate with your passkey.
                        </p>
                        <label className="login-page-label" htmlFor="accountId">
                            Account ID
                        </label>
                        <input
                            id="accountId"
                            className="login-page-input"
                            type="text"
                            value={accountIdInput}
                            onChange={(e) => onAccountIdChange(e.target.value)}
                            placeholder="Paste your Account ID here"
                            disabled={busy}
                            autoComplete="username"
                            spellCheck={false}
                        />
                        <button
                            className="login-page-btn"
                            onClick={onLogin}
                            disabled={busy || !accountIdInput.trim()}
                            aria-busy={busy}
                        >
                            {busy ? 'Signing in…' : 'Sign In with Passkey'}
                        </button>
                        <p className="login-page-hint">
                            Your Account ID was shown after registration. It is also saved in your browser's localStorage under <code>qreditcharge_accountId</code>.
                        </p>
                    </div>
                )}

                {/* Error message */}
                {errorMessage && (
                    <div className="login-page-error" role="alert" aria-live="assertive">
                        {errorMessage}
                    </div>
                )}
            </div>
        </main>
    );
};

export default LoginPageView;
